import { Playbook, PlaybookStatus } from '@driftt/api-types'
import { Button, ButtonProps, Li, Ul } from '@driftt/tide-core'
import { Modal, ModalBody, ModalFooter, ModalTitle } from '@driftt/tide-modal'
import { showTransientNotification } from '@driftt/traffic-control'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'

import { savePlaybookStatus } from 'api/playbooks'

interface InstallReminderAction {
  showLoading?: boolean
  style?: React.CSSProperties
  text: string
  toggleOn?: boolean
  type: ButtonProps['type']
}

const INSTALL_REMINDERS: Record<
  string,
  { body: JSX.Element; actions: InstallReminderAction[] }
> = {
  [PlaybookStatus.DISABLED]: {
    body: (
      <>
        In order for the Fastlane playbook to fire after a form submission,
        please make sure:
        <Ul>
          <Li>The playbook is turned on</Li>
          <Li>
            Your teammate has installed the JavaScript in the form submission
            flow
          </Li>
        </Ul>
      </>
    ),
    actions: [
      {
        showLoading: true,
        text: 'Turn playbook on',
        toggleOn: true,
        type: 'primary',
      },
      {
        style: { marginLeft: '8px' },
        text: 'Keep playbook off',
        type: 'tertiary',
      },
    ],
  },
  [PlaybookStatus.ENABLED]: {
    body: (
      <>
        In order for the Fastlane playbook to fire after a form submission,
        please make sure:
        <Ul>
          <Li>
            Your teammate has installed the JavaScript in the form submission
            flow
          </Li>
        </Ul>
      </>
    ),
    actions: [
      {
        text: 'Okay!',
        type: 'primary',
      },
    ],
  },
}

interface Props {
  id: number
  onClose: () => void
  open: boolean
  status: PlaybookStatus
  updateStatus: (playbook: Playbook) => void
}

const FastLaneInstallReminderModal = ({
  id,
  onClose,
  open,
  status,
  updateStatus,
}: Props) => {
  const dispatch = useDispatch()
  const [isSaving, setIsSaving] = useState(false)

  const { actions = [], body = null } = INSTALL_REMINDERS[status] || {}

  const togglePlaybookOn = async () => {
    try {
      setIsSaving(true)
      const updatedStatus =
        status === PlaybookStatus.DISABLED
          ? PlaybookStatus.ENABLED
          : PlaybookStatus.DISABLED
      const { data } = await savePlaybookStatus({
        id,
        status: updatedStatus,
      })

      dispatch(
        showTransientNotification({
          message: `Your playbook has been ${updatedStatus.toLowerCase()}`,
        })
      )

      updateStatus(data)
      setIsSaving(false)
    } catch (err) {
      console.warn(err)
      setIsSaving(false)
    }

    onClose()
  }

  return (
    <Modal open={open} onClose={onClose} size="small">
      <ModalTitle>Playbook successfully published</ModalTitle>
      <ModalBody>{body}</ModalBody>
      <ModalFooter>
        {actions.map(({ showLoading, style, text, toggleOn, type }, i) => (
          <Button
            key={i}
            loading={showLoading ? isSaving : false}
            onClick={toggleOn ? togglePlaybookOn : onClose}
            style={style}
            type={type}
          >
            {text}
          </Button>
        ))}
      </ModalFooter>
    </Modal>
  )
}

export default FastLaneInstallReminderModal
